// 전역 검색 모달 — 열려있는 모든 스크립트/프로젝트를 대상으로 텍스트 검색
// 결과 클릭 시 해당 프로젝트/파일로 전환 + 매칭 라인으로 스크롤 (onNavigate)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiSearch } from 'react-icons/fi';
import './GlobalSearchModal.css';

const MAX_MATCHES_PER_FILE = 500;   // 단일 파일에서 과도한 히트 방지
const MAX_TOTAL_MATCHES = 2000;     // 전역 상한

function searchAll(query, openFiles, { caseSensitive = false } = {}) {
    if (!query || query.length === 0) return [];
    const q = caseSensitive ? query : query.toLowerCase();
    const results = [];
    let totalCount = 0;
    for (const file of openFiles) {
        if (totalCount >= MAX_TOTAL_MATCHES) break;
        const lines = (file.content || '').split('\n');
        let fileCount = 0;
        for (let i = 0; i < lines.length; i++) {
            if (fileCount >= MAX_MATCHES_PER_FILE) break;
            const line = lines[i];
            const searchLine = caseSensitive ? line : line.toLowerCase();
            let pos = 0;
            while ((pos = searchLine.indexOf(q, pos)) !== -1) {
                results.push({
                    fileId: file.id,
                    fileName: file.name,
                    projectId: file.projectId || null,
                    lineNumber: i + 1,
                    lineText: line,
                    matchStart: pos,
                    matchEnd: pos + q.length
                });
                fileCount += 1;
                totalCount += 1;
                if (fileCount >= MAX_MATCHES_PER_FILE) break;
                if (totalCount >= MAX_TOTAL_MATCHES) break;
                pos += q.length > 0 ? q.length : 1;
            }
        }
    }
    return results;
}

// 결과를 프로젝트 → 파일 2단계로 그룹화
function groupResults(results, projects, t) {
    const projById = new Map(projects.map(p => [p.id, p]));
    const byProject = new Map();   // projectId|null → { name, files: Map<fileId, { name, matches: [] }> }
    for (const r of results) {
        const pid = r.projectId || null;
        if (!byProject.has(pid)) {
            byProject.set(pid, {
                projectId: pid,
                name: pid ? (projById.get(pid)?.name || t('common:gs.deletedProject')) : t('common:gs.standaloneScript'),
                icon: pid ? '📁' : '📄',
                files: new Map()
            });
        }
        const group = byProject.get(pid);
        if (!group.files.has(r.fileId)) {
            group.files.set(r.fileId, { fileId: r.fileId, name: r.fileName, matches: [] });
        }
        group.files.get(r.fileId).matches.push(r);
    }
    return Array.from(byProject.values()).map(g => ({
        ...g,
        files: Array.from(g.files.values())
    }));
}

const GlobalSearchModal = ({ isOpen, onClose, openFiles = [], projects = [], onNavigate }) => {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [caseSensitive, setCaseSensitive] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            // 열릴 때 자동 포커스
            setTimeout(() => inputRef.current?.focus(), 20);
            setTimeout(() => inputRef.current?.select(), 30);
        }
    }, [isOpen]);

    // 디바운스 (150ms)
    useEffect(() => {
        const h = setTimeout(() => setDebouncedQuery(query), 150);
        return () => clearTimeout(h);
    }, [query]);

    const results = useMemo(() => {
        if (!isOpen || !debouncedQuery) return [];
        return searchAll(debouncedQuery, openFiles, { caseSensitive });
    }, [isOpen, debouncedQuery, openFiles, caseSensitive]);

    const grouped = useMemo(() => groupResults(results, projects, t), [results, projects, t]);

    if (!isOpen) return null;

    const totalCount = results.length;
    const fileCount = grouped.reduce((acc, g) => acc + g.files.length, 0);

    const renderSnippet = (r) => {
        const before = r.lineText.substring(Math.max(0, r.matchStart - 40), r.matchStart);
        const matched = r.lineText.substring(r.matchStart, r.matchEnd);
        const after = r.lineText.substring(r.matchEnd, r.matchEnd + 80);
        return (
            <>
                <span className="gs-snippet-prefix">{before}</span>
                <mark className="gs-snippet-match">{matched}</mark>
                <span className="gs-snippet-suffix">{after}</span>
            </>
        );
    };

    const handleClickMatch = (r) => {
        if (typeof onNavigate === 'function') {
            onNavigate({ projectId: r.projectId, fileId: r.fileId, lineNumber: r.lineNumber });
        }
    };

    return (
        <div className="gs-overlay" onMouseDown={onClose}>
            <div className="gs-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="gs-header">
                    <h3><FiSearch style={{ verticalAlign: 'middle', marginRight: 6 }} />{t('common:gs.title')}</h3>
                    <button className="gs-close" onClick={onClose} title={t('common:gs.close')}><FiX size={18} /></button>
                </div>
                <div className="gs-toolbar">
                    <input
                        ref={inputRef}
                        className="gs-input"
                        type="text"
                        placeholder={t('common:gs.placeholder')}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
                    />
                    <label className="gs-opt">
                        <input
                            type="checkbox"
                            checked={caseSensitive}
                            onChange={(e) => setCaseSensitive(e.target.checked)}
                        />
                        {t('common:gs.caseSensitive')}
                    </label>
                    <span className="gs-stats">
                        {debouncedQuery ? t('common:gs.summary', { total: totalCount, files: fileCount }) : t('common:gs.enterTerm')}
                    </span>
                </div>
                <div className="gs-results">
                    {grouped.length === 0 && debouncedQuery && (
                        <div className="gs-empty">{t('common:gs.noResults')}</div>
                    )}
                    {grouped.map(group => (
                        <div key={group.projectId || '__default__'} className="gs-group">
                            <div className="gs-group-head">
                                <span className="gs-group-icon">{group.icon}</span>
                                <span className="gs-group-name">{group.name}</span>
                                <span className="gs-group-count">{t('common:gs.nMatches', { n: group.files.reduce((a, f) => a + f.matches.length, 0) })}</span>
                            </div>
                            {group.files.map(file => (
                                <div key={file.fileId} className="gs-file">
                                    <div className="gs-file-head">
                                        <span className="gs-file-name">{file.name}</span>
                                        <span className="gs-file-count">{file.matches.length}</span>
                                    </div>
                                    {file.matches.map((m, idx) => (
                                        <div
                                            key={idx}
                                            className="gs-match"
                                            onClick={() => handleClickMatch(m)}
                                            title={t('common:gs.gotoLine', { name: file.name, line: m.lineNumber })}
                                        >
                                            <span className="gs-line-no">L{m.lineNumber}</span>
                                            <span className="gs-snippet">{renderSnippet(m)}</span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ))}
                    {totalCount >= MAX_TOTAL_MATCHES && (
                        <div className="gs-empty">{t('common:gs.tooMany', { max: MAX_TOTAL_MATCHES })}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GlobalSearchModal;
